import { useCallback, useRef } from 'react';
import { OTPInput, SlotProps } from 'input-otp';
import { EventCollectorProps } from 'hooks/context/EventCollector/types';
import useDebounce from 'hooks/useDebounce';
import useEventCollector from 'hooks/context/EventCollector/useEventCollector';
import TextInputEvent from 'hooks/context/EventCollector/events/TextInputEvent';
import Slot from './Slot';
import { ONLY_NUMBERS_REGEXP } from '../../constants';

export type Props = {
  value: string;
  length?: number;
  onChange?: (newValue: string) => void;
  // onInput is used when you need the current value as an event-like object
  onInput?: (e: { target: { value: string } }) => void;
  dataTest?: string;
  name: string;
  isError?: boolean;
  disabled?: boolean;
  className?: string;
  pattern?: string;
};

function OTP({
  value,
  onChange,
  onInput,
  length = 4,
  name,
  isError,
  eventCollector,
  dataTest = 'otp',
  disabled = false,
  pattern = ONLY_NUMBERS_REGEXP, // default only-numeric characters allowed
}: Props & EventCollectorProps): JSX.Element {
  const { track } = useEventCollector();
  const valueRef = useRef(value);

  const handleEventCollector = useCallback(
    (newValue: string) => {
      if (eventCollector?.dontTrack || valueRef.current === newValue) {
        return;
      }

      track(
        new TextInputEvent({
          value: eventCollector?.dontTrackContent
            ? '<not tracked>'
            : (eventCollector?.contentOverride ?? newValue),
          description: eventCollector?.description ?? name ?? '',
        }),
      );

      valueRef.current = newValue;
    },
    [
      eventCollector?.contentOverride,
      eventCollector?.description,
      eventCollector?.dontTrack,
      eventCollector?.dontTrackContent,
      name,
      track,
    ],
  );

  useDebounce(() => handleEventCollector(value), 1000, [value]);

  const renderSlots = (slots: SlotProps[]) => {
    return (
      <div className="flex space-x-2">
        {slots.map((slot, idx) => (
          <Slot key={idx} {...slot} isError={isError} />
        ))}
      </div>
    );
  };

  return (
    <OTPInput
      value={value}
      name={name}
      onInput={
        onInput
          ? (event) =>
              onInput(event as unknown as { target: { value: string } })
          : undefined
      }
      onChange={onChange}
      maxLength={length}
      data-test={dataTest}
      inputMode="numeric"
      render={({ slots }) => renderSlots(slots)}
      disabled={disabled}
      pattern={pattern}
    />
  );
}

export default OTP;
